import type { GoalSlug } from '@/lib/aviado/goals';

/**
 * Research CTA module — config + deterministic resolvers.
 *
 * Ported from the Claude Design handoff (`CTA design/aviado-labs-redesign/
 * project/cta.js`). One reusable component (`components/cta/ResearchCTA.tsx`)
 * that reframes "get the app" as "turn research into an evidence-based
 * stack", with inline Longevity Daily capture.
 *
 * SEO / INDEXING INVARIANTS (do not relax):
 *  - Treatment + headline-term selection is DETERMINISTIC per URL: hash of
 *    the page slug only. No Date.now(), no cookies, no headers, no
 *    Math.random() anywhere in the resolution path. A given URL emits
 *    identical HTML to every visitor and to Googlebot (rendered in the
 *    initial SSR/ISR markup) — page-derived templating, not cloaking.
 *  - The beta button href carries NO utm params: /research/* and /get-app
 *    share www.aviado.com + one GA4 property, so utm on internal navigation
 *    would reset sessions and clobber true acquisition source. Attribution
 *    comes from the GA4 click event (treatment/context/variant props) +
 *    document.referrer recorded by the join-ios-beta edge function.
 *  - stackTerms must stay honestly descriptive of their pillar — accurate
 *    synonyms are clean SEO; off-topic keyword-baiting draws penalties.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CtaContext = 'pillar' | 'article' | 'biomarker' | 'supplement' | 'generic';
export type CtaVariant = 'sidebar' | 'inline' | 'compact' | 'endcap' | 'rail';
export type CtaTreatment = 'stack' | 'qr' | 'outcome' | 'waitlist';

export interface CtaTreatmentDef {
  id: CtaTreatment;
  /** Mono kicker line, e.g. "Aviado · Now in private beta". */
  kicker: string;
  /**
   * Headline parts. `emphasis` renders italic-teal via <em>. When `term` is
   * present the stack/qr headlines interpolate it as the emphasis.
   */
  headline: (term: string, marker: string) => { pre: string; emphasis: string; post: string };
  /**
   * Compact-variant headline (sidebar/compact). Falls back to `headline`
   * when omitted.
   */
  headlineShort?: (term: string, marker: string) => { pre: string; emphasis: string; post: string };
  /** Full dek (endcap/inline). Falls back to the context dek when null. */
  dek: ((topic: string, marker: string) => string) | null;
  /** Short dek for sidebar/compact. */
  dekShort: string;
  button: string;
  newsletter: 'inline' | 'hero' | 'none';
  showQR: boolean;
  showBadges: boolean;
}

// ---------------------------------------------------------------------------
// Copy — context deks (topicName interpolation)
// ---------------------------------------------------------------------------

// Voice rule (2026-06-06 review): Aviado is the actor in headlines and deks
// ("The Aviado app tracks…"), with short imperative action chains for the
// how ("Build your stack; log your labs; track what changes"). Button labels
// stay imperative — that's UI convention, not prose.
export const CTA_CONTEXT_DEK: Record<CtaContext, (topic: string) => string> = {
  generic: () =>
    'The Aviado app turns this research into a supplement plan tied to your goals, biomarkers, and the evidence that actually moves them.',
  pillar: (t) =>
    `The Aviado app turns the ${t} research into a supplement plan tied to your goals, biomarkers, and the evidence that actually moves them.`,
  article: (t) =>
    `The Aviado app shows where ${t} fits — build your stack and track whether it actually moves your biomarkers over time.`,
  biomarker: (t) =>
    `The Aviado app ranks the supplements with real evidence for ${t}. Build your stack. Track it as you re-test.`,
  supplement: (t) =>
    `The Aviado app shows where ${t} fits in an evidence-based stack — tied to your goals and the biomarkers it moves.`,
};

// ---------------------------------------------------------------------------
// A/B treatments (message angle)
// ---------------------------------------------------------------------------

export const CTA_TREATMENTS: Record<CtaTreatment, CtaTreatmentDef> = {
  /* A — control: stack-led, editorial, inline newsletter */
  stack: {
    id: 'stack',
    kicker: 'Aviado · Now in private beta',
    headline: (term) =>
      term
        ? { pre: 'Aviado turns this research into your ', emphasis: `${term} stack`, post: '' }
        : { pre: 'Aviado turns research into ', emphasis: 'evidence-based stacks', post: '' },
    dek: null,
    dekShort: 'Build your stack. Weigh the evidence. Adjust as it evolves.',
    button: 'Join the beta',
    newsletter: 'inline',
    showQR: false,
    showBadges: true,
  },
  /* B — QR + early access, scan-to-join */
  qr: {
    id: 'qr',
    kicker: 'Aviado · Private beta',
    headline: (term) =>
      term
        ? { pre: 'Aviado builds your ', emphasis: `${term} stack`, post: ' — be first in the beta' }
        : { pre: 'Aviado builds ', emphasis: 'evidence-based stacks', post: ' — be first in the beta' },
    dek: () =>
      'Aviado is in private beta on web, with iOS & Android landing soon. Scan to claim your spot — or join from this device.',
    dekShort: 'Private beta on web. Join from any device.',
    button: 'Claim your beta spot',
    newsletter: 'none',
    showQR: true,
    showBadges: true,
  },
  /* C — outcome / biomarker-led */
  outcome: {
    id: 'outcome',
    kicker: 'Aviado · Free during beta',
    headline: (_term, marker) =>
      marker
        ? { pre: 'The Aviado app tracks the supplements that actually ', emphasis: `move your ${marker}`, post: '' }
        : { pre: 'The Aviado app tracks what actually ', emphasis: 'moves your biomarkers', post: '' },
    dek: (_t, m) =>
      `Build your stack. Log your labs. Track what changes over time${m ? ` — starting with your ${m}` : ''}. Free for every beta member.`,
    dekShort: 'Build your stack. Log your labs. Track what changes.',
    button: 'Get beta access',
    newsletter: 'inline',
    showQR: false,
    showBadges: true,
  },
  /* D — waitlist + newsletter-first (newsletter is the hero). The term
     qualifies the evidence ("the heart health evidence") so waitlist pages
     keep the page-contextual tailoring the other treatments get. */
  waitlist: {
    id: 'waitlist',
    kicker: 'Aviado Longevity Daily · + beta waitlist',
    headline: (term) => ({
      pre: term ? `Aviado Longevity Daily delivers the ${term} evidence today. ` : 'Aviado Longevity Daily delivers the evidence today. ',
      emphasis: 'The Aviado app is coming.',
      post: '',
    }),
    headlineShort: (term) => ({
      pre: term ? `The ${term} evidence, daily. ` : 'The evidence, daily. ',
      emphasis: 'The app is coming.',
      post: '',
    }),
    dek: () =>
      'Aviado Longevity Daily delivers the most important evidence-backed longevity news — and puts you first in line for the beta.',
    dekShort: 'Evidence-backed longevity news, daily. First for the app.',
    button: 'Join the beta waitlist',
    newsletter: 'hero',
    showQR: false,
    showBadges: false,
  },
};

// ---------------------------------------------------------------------------
// Newsletter copy
//
// The capture path is the shared `join-ios-beta` edge function, which bundles
// Longevity Daily with a beta-waitlist spot (double-opt-in confirmation
// email). The labels disclose that bundling honestly — do not revert to
// newsletter-only phrasing while the backend couples the two.
// ---------------------------------------------------------------------------

export const CTA_NEWSLETTER = {
  label: 'Get **Aviado Longevity Daily** — the most important evidence-backed longevity news in your inbox, plus a spot in the beta line.',
  labelHero: 'Get **Aviado Longevity Daily** — the most important evidence-backed longevity news in your inbox, daily and free.',
  placeholder: 'you@example.com',
  button: 'Subscribe',
  buttonHero: 'Get the daily brief',
  success: "Check your inbox — confirm your email and you're in.",
  error: 'Something went wrong. Try again.',
} as const;

/** Single destination for every primary CTA action. No utm params (see header). */
export const CTA_BETA_URL = 'https://www.aviado.com/get-app';

// ---------------------------------------------------------------------------
// Page-contextual headline terms — honest synonyms per pillar
// ---------------------------------------------------------------------------

export const PILLAR_STACK_TERMS: Record<GoalSlug, string[]> = {
  'brain-cognitive': ['cognitive health', 'brain performance', 'neuroprotective'],
  cardiovascular: ['heart health', 'cardiovascular', 'cardioprotective'],
  metabolic: ['metabolic health', 'blood-sugar', 'metabolic'],
  immune: ['immune support', 'immune resilience'],
  'muscle-bone-joint': ['strength & recovery', 'musculoskeletal'],
  longevity: ['longevity', 'healthspan'],
  hormonal: ['hormonal balance', 'hormone health'],
  sleep: ['sleep quality', 'sleep'],
  gut: ['gut health', 'microbiome'],
  'skin-hair': ['skin & hair', 'skin health'],
};

// ---------------------------------------------------------------------------
// Deterministic resolvers
// ---------------------------------------------------------------------------

/**
 * Mid-sentence noun form for display names interpolated into CTA copy.
 * Title-case words downcase ("Omega-3 Index" → "omega-3 index",
 * "Metabolic Health" → "metabolic health"); words with internal capitals
 * are acronym-like and keep their canonical casing ("HbA1c", "hs-CRP",
 * "ApoB").
 */
export function ctaNoun(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => (w.slice(1) === w.slice(1).toLowerCase() ? w.toLowerCase() : w))
    .join(' ');
}

/** Stable 32-bit string hash (same construction as the design prototype). */
export function hashString(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h;
}

const TREATMENT_ORDER: CtaTreatment[] = ['stack', 'qr', 'outcome', 'waitlist'];

/**
 * Per-URL treatment arm. Deterministic: a given seed (page slug) always
 * lands the same treatment, for every visitor and crawler. Salted so the
 * treatment hash doesn't correlate with the term hash on the same slug.
 */
export function resolveTreatment(seed: string): CtaTreatment {
  return TREATMENT_ORDER[hashString(`treatment:${seed}`) % TREATMENT_ORDER.length];
}

export interface ResolveTermInput {
  /** Explicit term — wins outright. */
  stackTerm?: string;
  /** Synonym list (2–3 per pillar). One is picked deterministically. */
  stackTerms?: string[];
  /** Explicit A/B bucket index — overrides the seed pick. Never random. */
  termIndex?: number;
  /** Stable seed (slug/id) for the deterministic pick. */
  termSeed?: string;
}

export function resolveStackTerm(input: ResolveTermInput): string {
  if (input.stackTerm) return input.stackTerm;
  const list = (input.stackTerms ?? []).filter(Boolean);
  if (list.length === 0) return '';
  const idx =
    typeof input.termIndex === 'number'
      ? input.termIndex
      : hashString(String(input.termSeed ?? list.join('|')));
  return list[((idx % list.length) + list.length) % list.length];
}

// ---------------------------------------------------------------------------
// ctaPropsFor — canonical message-tailoring resolver
// ---------------------------------------------------------------------------

export interface CtaResolvedProps {
  context: CtaContext;
  topicName: string;
  stackTerms: string[];
  marker: string;
  termSeed: string;
  treatment: CtaTreatment;
}

/**
 * One place that maps any page entity → ResearchCTA props, encoding the
 * inheritance chain: entity's own terms → owning pillar's terms → none.
 * Article/biomarker/supplement pages inherit their pillar's stackTerms;
 * the seed is the page's own slug so sibling pages vary deterministically.
 */
export function ctaPropsFor(entity: {
  context: CtaContext;
  /** Page slug — seeds both term pick and treatment arm. */
  slug: string;
  /** Display name interpolated into the dek. */
  topicName: string;
  /** Owning pillar (v2.7 goal slug) for term inheritance. */
  pillarSlug?: GoalSlug | string;
  /** Biomarker noun for the outcome treatment, e.g. "omega-3 index". */
  marker?: string;
  /** Pin a treatment instead of the per-URL hash (e.g. outcome on biomarkers). */
  treatment?: CtaTreatment;
}): CtaResolvedProps {
  const stackTerms =
    (entity.pillarSlug && PILLAR_STACK_TERMS[entity.pillarSlug as GoalSlug]) || [];
  return {
    context: entity.context,
    topicName: entity.topicName,
    stackTerms,
    marker: entity.marker ?? '',
    termSeed: entity.slug,
    treatment: entity.treatment ?? resolveTreatment(entity.slug),
  };
}
